class BackendAPI {
  constructor() {
    this.accessToken = window.customer?.accessToken || "";
    this.apiUrl = window.BACKEND_BASE_URL;

    this.headers = {
      Authorization: `Bearer ${this.accessToken}`,
      Accept: "application/json",
    };
  }

  async addToWishlist(productVariantId) {
    // Check if user is authenticated with JWT
    if (this.accessToken) {
      // Existing authenticated flow
      return fetch(`${this.apiUrl}/customers/wishlist`, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ productVariantId }),
      })
        .then((res) => res.json())
        .catch((_) => null);
    }

    // Guest flow: try to get email from session storage
    const showContext = this.getShowContext();
    if (showContext && showContext.email) {
      return fetch(`${this.apiUrl}/shows/${showContext.shareId}/wishlist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: showContext.email,
          productVariantId,
        }),
      })
        .then((res) => res.json())
        .catch((_) => null);
    }

    // Check for stored guest contact (from favorites modal)
    const guestContact = this.getGuestContact();
    if (guestContact && guestContact.email) {
      return this.addToGuestWishlist(productVariantId, guestContact);
    }

    // No auth, no show context, no guest contact - cannot add to wishlist
    console.warn("Cannot add to wishlist: no authentication, show context, or guest contact");
    return null;
  }

  // Helper method to extract show context from localStorage
  getShowContext() {
    try {
      // Get show ID from localStorage
      const showShareId = localStorage.getItem("CURRENT_SHOW_ID");

      if (!showShareId) return null;

      // Get check-in data from session storage, fall back to localStorage
      const checkInKey = `SHOW_CHECKED_IN_${showShareId}`;
      const checkInData = sessionStorage.getItem(checkInKey) || localStorage.getItem(checkInKey);

      if (!checkInData) return null;

      const data = JSON.parse(checkInData);

      // Verify not expired (only applies to sessionStorage data with expiresAt)
      if (data.expiresAt && Date.now() > data.expiresAt) {
        sessionStorage.removeItem(checkInKey);
        return null;
      }

      if (data.checkedIn && data.email) {
        return {
          shareId: showShareId,
          email: data.email,
        };
      }
    } catch (e) {
      console.error("Error getting show context:", e);
    }

    return null;
  }

  // Save guest contact info to session storage (from favorites modal)
  saveGuestContact(data) {
    try {
      sessionStorage.setItem('GUEST_CONTACT', JSON.stringify({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || ''
      }));
    } catch (e) {
      console.error('Error saving guest contact:', e);
    }
  }

  // Get stored guest contact from session storage
  getGuestContact() {
    try {
      const data = sessionStorage.getItem('GUEST_CONTACT');
      if (!data) return null;
      return JSON.parse(data);
    } catch (e) {
      console.error('Error getting guest contact:', e);
      return null;
    }
  }

  // Get partner context (stylistId) from cached partner data
  getPartnerContext() {
    try {
      // Check for show-based partner
      const showShareId = localStorage.getItem('CURRENT_SHOW_ID');
      if (showShareId) {
        const showPartner = sessionStorage.getItem(`CACHE_PARTNER_SHOW_${showShareId}`);
        if (showPartner) {
          const parsed = JSON.parse(showPartner);
          if (parsed && parsed.stylistId) return { stylistId: parsed.stylistId };
        }
      }

      // Check URL for partner/stylist context
      const urlParams = new URLSearchParams(window.location.search);
      const urlStylistId = urlParams.get('stylist') || urlParams.get('stylistId');
      if (urlStylistId) {
        return { stylistId: urlStylistId };
      }

      // Check localStorage for CURRENT_STYLIST_ID
      const localStylistId = localStorage.getItem('CURRENT_STYLIST_ID');
      if (localStylistId && localStylistId !== 'NONE') {
        return { stylistId: localStylistId };
      }

      return null;
    } catch (e) {
      console.error('Error getting partner context:', e);
      return null;
    }
  }

  // Add to wishlist as guest (unauthenticated, no show context)
  async addToGuestWishlist(productVariantId, guestContact) {
    const partnerContext = this.getPartnerContext();

    return fetch(`${this.apiUrl}/guests/wishlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        firstName: guestContact.firstName,
        lastName: guestContact.lastName,
        email: guestContact.email,
        phone: guestContact.phone || '',
        productVariantId,
        stylistId: partnerContext?.stylistId || '',
      }),
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to add to wishlist');
        }
      })
      .catch((error) => {
        console.error('Guest wishlist error:', error);
        return null;
      });
  }

  async removeFromWishlist(productVariantId) {
    return fetch(`${this.apiUrl}/customers/wishlist`, {
      method: "DELETE",
      headers: this.headers,
      body: JSON.stringify({ productVariantId }),
    })
      .then((res) => res.json())
      .catch((_) => null);
  }

  // Derive the StylistType attribution value from a partner's tags.
  // Denim stylists ship direct-to-customer only, so checkout uses this to
  // hide the "Ship to Show" option. Kept in one place so every site that
  // persists CURRENT_STYLIST_TYPE agrees on the tag rules. Match tags
  // case-insensitively. Returns one of:
  //   "denim_stylist" | "denim_pilot_stylist" | "standard"
  // Callers persist "NONE" separately when no stylist is attributed.
  deriveStylistType(tags) {
    const normalized = Array.isArray(tags)
      ? tags.map((tag) => String(tag).toLowerCase())
      : [];
    if (normalized.includes("denim_stylist")) return "denim_stylist";
    if (normalized.includes("denim_pilot_stylist")) return "denim_pilot_stylist";
    return "standard";
  }

  async getPartnerBySite(site, checkCache = true) {
    const cacheKey = `CACHE_PARTNER_${site}`;

    if (checkCache) {
      const cachedPartner = sessionStorage.getItem(cacheKey);
      if (cachedPartner && cachedPartner != "null") {
        const parsed = JSON.parse(cachedPartner);
        // Invalidate cache if tags field is missing (old cached data)
        if (parsed && !parsed.hasOwnProperty("tags")) {
          sessionStorage.removeItem(cacheKey);
        } else {
          return parsed;
        }
      }
    }

    return fetch(`${this.apiUrl}/partners?site=${site}`)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          return data;
        } else {
          sessionStorage.removeItem(cacheKey);
          return null;
        }
      })
      .catch((_) => {
        sessionStorage.removeItem(cacheKey);
        return null;
      });
  }

  async getPartnerByStylistId(stylistId) {
    const cacheKey = `CACHE_PARTNER_STYLIST_${stylistId}`;

    const cachedPartner = sessionStorage.getItem(cacheKey);
    if (cachedPartner && cachedPartner !== "null") {
      const parsed = JSON.parse(cachedPartner);
      // Invalidate cache if tags field is missing (old cached data)
      if (parsed && !parsed.hasOwnProperty("tags")) {
        sessionStorage.removeItem(cacheKey);
      } else {
        return parsed;
      }
    }

    return fetch(`${this.apiUrl}/partners?stylistId=${stylistId}`)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          return data;
        } else {
          sessionStorage.removeItem(cacheKey);
          return null;
        }
      })
      .catch((_) => {
        sessionStorage.removeItem(cacheKey);
        return null;
      });
  }

  async getPartnerByShowId(showId) {
    const cacheKey = `CACHE_PARTNER_SHOW_${showId}`;

    const cachedPartner = sessionStorage.getItem(cacheKey);
    if (cachedPartner && cachedPartner !== "null") {
      const parsed = JSON.parse(cachedPartner);
      // Invalidate cache if tags field is missing (old cached data)
      if (parsed && !parsed.hasOwnProperty("tags")) {
        sessionStorage.removeItem(cacheKey);
      } else {
        return parsed;
      }
    }

    return fetch(`${this.apiUrl}/partners?showId=${showId}`)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          return data;
        } else {
          sessionStorage.removeItem(cacheKey);
          return null;
        }
      })
      .catch((_) => {
        sessionStorage.removeItem(cacheKey);
        return null;
      });
  }

  async getShowById(showId) {
    const cacheKey = `CACHE_SHOW_${showId}`;
    const cachedShow = sessionStorage.getItem(cacheKey);
    if (cachedShow) {
      return JSON.parse(cachedShow);
    }

    return fetch(`${this.apiUrl}/shows?shareId=${showId}`)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          return data;
        } else {
          sessionStorage.setItem(cacheKey, null);
          return null;
        }
      })
      .catch((_) => {
        sessionStorage.setItem(cacheKey, null);
        return null;
      });
  }

  async syncPreferredStylist(site) {
    return fetch(`${this.apiUrl}/customers/affinity`, {
      method: "PATCH",
      headers: this.headers,
      body: JSON.stringify({ site }),
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          return data;
        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async getPartnerByCustomerEmail() {
    return fetch(`${this.apiUrl}/customers/stylists`, {
      method: "GET",
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          return data;

        } else {
          return null;
        }
      })
      .catch((_) => {
        return null;
      });
  }

  async checkInToShow(showShareId, guestData) {
    return fetch(`${this.apiUrl}/shows/${showShareId}/checkin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(guestData),
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.ok) {
          return data;
        } else {
          throw new Error(data.message || "Check-in failed");
        }
      })
      .catch((error) => {
        console.error("Check-in error:", error);
        throw error;
      });
  }

  async getInventoryPromisedBy(variantId) {
    return fetch(`${this.apiUrl}/public/inventory/promised-by/${variantId}`, {
      method: "GET",
      headers: this.headers,
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          return data;
        } else {
          return null;
        }
      })
      .catch((error) => {
        console.error("Error fetching inventory data:", error);
        return null;
      });
  }

  async submitEnrollmentInquiry(payload) {
    return fetch(`${this.apiUrl}/customers/leads`, {
      method: "POST",
      headers: {
        ...this.headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to submit enrollment inquiry.");
        }
      })
      .catch((error) => {
        console.error("Enrollment inquiry submit error:", error);
        return null;
      });
  }

  // CAB-3244: Denim-only stylist 15% new-to-cabi signup.
  // Creates a CRM contact for the stylist, applies Email/SMS marketing
  // opt-in (Shopify + Sailthru), checks purchase-based eligibility, and
  // returns a per-customer single-use discount code when eligible.
  // Behaves like the guest wishlist / show check-in submits above:
  // resolves stylist attribution from partner context and never throws to
  // the caller (returns null on failure so the UI can show a generic error).
  async submitDenimSignup(payload) {
    const partnerContext = this.getPartnerContext();

    return fetch(`${this.apiUrl}/guests/denim-signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        phone: payload.phone || "",
        emailOptIn: payload.emailOptIn === true,
        smsOptIn: payload.smsOptIn === true,
        site: payload.site || localStorage.getItem("CURRENT_SITE") || "",
        stylistId: payload.stylistId || partnerContext?.stylistId || "",
      }),
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to submit denim signup.");
        }
      })
      .catch((error) => {
        console.error("Denim signup submit error:", error);
        return null;
      });
  }

  async submitEmailSignup(email) {
    return fetch(`${this.apiUrl}/guests/email-signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ email }),
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to submit email signup.");
        }
      })
      .catch((error) => {
        console.error("Email signup submit error:", error);
        return null;
      });
  }

  async checkNewCustomer(email) {
    return fetch(`${this.apiUrl}/guests/new-customer-check?email=${encodeURIComponent(email)}`)
      .then((res) => (res.ok ? res.json() : null))
      .catch((error) => {
        console.error("New customer check error:", error);
        return null;
      });
  }
}

window.backend = new BackendAPI();
